// @ts-nocheck
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetBody, SheetFooter } from './ui/sheet';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { AdvancedDataTableV2, ColumnDef, BulkAction } from './advanced-data-table-v2';
import { 
  Plus, 
  MessageSquare, 
  Eye,
  Link,
  Lightbulb,
  Layers,
  Search,
  X,
  Calendar,
  User,
  Tag as TagIcon,
  Building,
  Star,
  MoreHorizontal,
  Archive,
  Trash2,
  Edit,
  Loader2,
  Upload
} from 'lucide-react';
import { supabase, type Feedback, type Client, type Tag } from '@product-radar/shared';
import { useWorkspace, useRegionalSettings } from '@/contexts/context';
import { hasPermission, type UserRole } from '@product-radar/shared';
import { formatCurrency, formatDate } from '@/lib/formatting';
import { ImportFeedbackDialog } from './import-feedback-dialog';
import { ProductNodeSelector } from './product-node-selector';

interface FeedbackItem {
  id: string;
  source: string;
  client_id: string;
  contributor: string;
  content: string;
  sentiment: string;
  status: string;
  priority?: number;
  rating?: number;
  raw_transcript?: string;
  product_area?: string;
  created_at: string;
  updated_at?: string;
  client?: {
    id: string;
    name: string;
    arr?: number;
  };
  clientArr?: number;
}

interface FeedbackManagementProps {
  onViewDetails: (id: string) => void;
}

function IdeaSelectorForFeedback({
  ideas,
  selectedIdeaIds,
  onSelectionChange
}: {
  ideas: { id: string; title: string; status: string }[];
  selectedIdeaIds: string[];
  onSelectionChange: (ids: string[]) => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredIdeas = searchQuery
    ? ideas.filter(i => i.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : ideas;
  const selectedIdeas = ideas.filter(i => selectedIdeaIds.includes(i.id));

  const toggleIdea = (ideaId: string) => {
    if (selectedIdeaIds.includes(ideaId)) {
      onSelectionChange(selectedIdeaIds.filter(id => id !== ideaId));
    } else {
      onSelectionChange([...selectedIdeaIds, ideaId]);
    }
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="flex items-center gap-1.5">
          <Lightbulb className="h-4 w-4" />
          Link to Ideas
        </Label>
        <p className="text-xs text-muted-foreground">Select which ideas this feedback relates to</p>
      </div>

      {selectedIdeas.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg border">
          {selectedIdeas.map(idea => (
            <Badge key={idea.id} variant="secondary" className="text-xs flex items-center space-x-1 pr-1">
              <span className="ml-1">{idea.title}</span>
              <button
                onClick={() => onSelectionChange(selectedIdeaIds.filter(id => id !== idea.id))}
                className="ml-1 hover:bg-muted rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search ideas..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <ScrollArea className="h-[200px] border rounded-lg p-2">
        {ideas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground text-sm">
            <Lightbulb className="h-8 w-8 mb-2 opacity-50" />
            <p>No ideas yet</p>
            <p className="text-xs mt-1">Create ideas first to link feedback</p>
          </div>
        ) : filteredIdeas.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
            No matching ideas
          </div>
        ) : (
          <div className="space-y-1">
            {filteredIdeas.map(idea => (
              <div
                key={idea.id}
                className={`flex items-center space-x-2 py-2 px-2 rounded hover:bg-muted/50 transition-colors cursor-pointer ${
                  selectedIdeaIds.includes(idea.id) ? 'bg-primary/5' : ''
                }`}
              >
                <Checkbox
                  checked={selectedIdeaIds.includes(idea.id)}
                  onCheckedChange={() => toggleIdea(idea.id)}
                  id={`idea-${idea.id}`}
                />
                <label htmlFor={`idea-${idea.id}`} className="flex-1 cursor-pointer text-sm">
                  {idea.title}
                  {idea.status && (
                    <span className="ml-2 text-xs text-muted-foreground">({idea.status})</span>
                  )}
                </label>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {ideas.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
          <span>{selectedIdeaIds.length} selected</span>
          {selectedIdeaIds.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => onSelectionChange([])}
            >
              Clear all
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function FeedbackManagement({ onViewDetails }: FeedbackManagementProps) {
  const { workspace, membership } = useWorkspace();
  const userRole = membership?.role as UserRole;
  const regionalSettings = useRegionalSettings();
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [ideas, setIdeas] = useState<{ id: string; title: string; status: string }[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState<FeedbackItem | null>(null);

  // Form state for new feedback
  const [newFeedback, setNewFeedback] = useState({
    client_id: '',
    contributor: '',
    source: 'Manual',
    content: '',
    sentiment: 'neutral' as 'positive' | 'negative' | 'neutral',
    status: 'New',
    product_item_ids: [] as string[],
    priority: 3 as 1 | 2 | 3 | 4 | 5,
    rating: undefined as number | undefined,
    linked_idea_ids: [] as string[]
  });

  // Form state for editing feedback
  const [editForm, setEditForm] = useState({
    client_id: '',
    contributor: '',
    source: '',
    content: '',
    sentiment: 'neutral' as 'positive' | 'negative' | 'neutral',
    status: 'New',
    priority: 3 as 1 | 2 | 3 | 4 | 5,
    product_item_ids: [] as string[],
    rating: undefined as number | undefined,
    linked_idea_ids: [] as string[],
  });

  useEffect(() => {
    if (workspace) {
      loadData();
    }
  }, [workspace]);

  const loadData = async () => {
    if (!workspace) return;
    
    setLoading(true);
    try {
      // Load feedback with related data (filtered by workspace)
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback')
        .select(`
          *,
          client:clients(id, name, arr),
          feedback_tags(tag:tags(name))
        `)
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      if (feedbackError) throw feedbackError;

      // Load product area mappings (from product definition / feedback_product_mapping)
      const feedbackIds = (feedbackData || []).map((f: any) => f.id);
      let productAreaByFeedbackId: Record<string, string> = {};
      if (feedbackIds.length > 0) {
        const { data: mappingData } = await supabase
          .from('feedback_product_mapping')
          .select('feedback_id, product_item:product_structure_items(name)')
          .in('feedback_id', feedbackIds);
        const feedbackToNames: Record<string, string[]> = {};
        (mappingData || []).forEach((m: any) => {
          const name = m.product_item?.name;
          if (name) {
            if (!feedbackToNames[m.feedback_id]) feedbackToNames[m.feedback_id] = [];
            feedbackToNames[m.feedback_id].push(name);
          }
        });
        productAreaByFeedbackId = Object.fromEntries(
          Object.entries(feedbackToNames).map(([id, names]) => [id, names.join(', ')])
        );
      }

      // Transform data to match component interface
      const transformedFeedback: FeedbackItem[] = (feedbackData || []).map((item: any) => ({
        id: item.id,
        source: item.source,
        client_id: item.client_id,
        contributor: item.contributor ?? item.author,
        content: item.content,
        sentiment: item.sentiment,
        status: item.status,
        createdAt: item.created_at,
        updated_at: item.updated_at,
        tags: item.feedback_tags?.map((ft: any) => ft.tag?.name).filter(Boolean) || [],
        priority: item.priority,
        rating: item.rating,
        raw_transcript: item.raw_transcript,
        product_area: productAreaByFeedbackId[item.id] || '',
        client: item.client,
        customer: item.client?.name || '',
        clientArr: item.client?.arr != null ? parseFloat(String(item.client.arr)) : undefined
      }));

      setFeedbackItems(transformedFeedback);

      // Load clients for dropdown (filtered by workspace)
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('name');

      if (clientsError) throw clientsError;
      setClients(clientsData || []);

      // Load ideas for linking (filtered by workspace)
      const { data: ideasData, error: ideasError } = await supabase
        .from('ideas')
        .select('id, title, status')
        .eq('workspace_id', workspace.id)
        .order('title');

      if (ideasError) throw ideasError;
      setIdeas(ideasData || []);

      // Load tags (filtered by workspace)
      const { data: tagsData, error: tagsError } = await supabase
        .from('tags')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('name');

      if (tagsError) throw tagsError;
      setTags(tagsData || []);

    } catch (error: any) {
      console.error('Error loading feedback data:', error);
      toast.error('Failed to load feedback data', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFeedback = async () => {
    if (!workspace) {
      toast.error('Workspace not loaded');
      return;
    }
    
    if (!newFeedback.client_id || !newFeedback.contributor || !newFeedback.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('feedback')
        .insert({
          workspace_id: workspace.id,
          client_id: newFeedback.client_id,
          contributor: newFeedback.contributor,
          source: newFeedback.source,
          content: newFeedback.content,
          sentiment: newFeedback.sentiment,
          product_area_id: null,
          priority: newFeedback.priority,
          rating: newFeedback.rating,
          status: newFeedback.status || 'New',
          created_by: membership?.user_id
        })
        .select()
        .single();

      if (error) throw error;

      const feedbackId = data.id;

      // Link to product structure items (from product definition)
      if (newFeedback.product_item_ids.length > 0) {
        const productLinks = newFeedback.product_item_ids.map((productItemId) => ({
          feedback_id: feedbackId,
          product_item_id: productItemId,
          product_id: null,
        }));
        const { error: productLinkError } = await supabase
          .from('feedback_product_mapping')
          .insert(productLinks);

        if (productLinkError) {
          console.error('Failed to link feedback to product areas:', productLinkError);
        }
      }

      // Link to ideas if selected
      if (newFeedback.linked_idea_ids.length > 0) {
        const linksToInsert = newFeedback.linked_idea_ids.map(ideaId => ({
          feedback_id: feedbackId,
          idea_id: ideaId
        }));
        const { error: linkError } = await supabase
          .from('feedback_ideas')
          .insert(linksToInsert);

        if (linkError) {
          console.error('Failed to link feedback to ideas:', linkError);
          toast.warning('Feedback created but could not link to ideas', {
            description: linkError.message
          });
        }
      }

      toast.success('Feedback created successfully!', {
        description: newFeedback.linked_idea_ids.length > 0
          ? `The feedback has been added and linked to ${newFeedback.linked_idea_ids.length} idea(s).`
          : 'The feedback has been added and will be processed shortly.'
      });

      setDialogOpen(false);
      setNewFeedback({
        client_id: '',
        contributor: '',
        source: 'Manual',
        content: '',
        sentiment: 'neutral',
        status: 'New',
        product_item_ids: [],
        priority: 3,
        rating: undefined,
        linked_idea_ids: []
      });
      
      loadData(); // Reload data
    } catch (error: any) {
      console.error('Error creating feedback:', error);
      toast.error('Failed to create feedback', {
        description: error.message
      });
    }
  };

  const handleEditFeedback = async (feedback: FeedbackItem) => {
    setEditingFeedback(feedback);
    let linkedIdeaIds: string[] = [];
    let productItemIds: string[] = [];
    try {
      const [{ data: ideaData }, { data: productData }] = await Promise.all([
        supabase.from('feedback_ideas').select('idea_id').eq('feedback_id', feedback.id),
        supabase.from('feedback_product_mapping').select('product_item_id').eq('feedback_id', feedback.id),
      ]);
      linkedIdeaIds = (ideaData || []).map((r: { idea_id: string }) => r.idea_id);
      productItemIds = (productData || []).map((r: { product_item_id: string }) => r.product_item_id).filter(Boolean);
    } catch {
      // ignore
    }
    setEditForm({
      client_id: feedback.client_id,
      contributor: feedback.contributor,
      source: feedback.source,
      content: feedback.content,
      sentiment: feedback.sentiment,
      status: feedback.status,
      priority: feedback.priority || 3,
      product_item_ids: productItemIds,
      rating: feedback.rating,
      linked_idea_ids: linkedIdeaIds,
    });
    setEditDialogOpen(true);
  };

  const handleUpdateFeedback = async () => {
    if (!editingFeedback) return;

    try {
      const { error } = await supabase
        .from('feedback')
        .update({
          client_id: editForm.client_id || null,
          contributor: editForm.contributor,
          source: editForm.source,
          content: editForm.content,
          sentiment: editForm.sentiment,
          status: editForm.status,
          priority: editForm.priority,
          product_area_id: null,
          rating: editForm.rating ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingFeedback.id);

      if (error) throw error;

      // Update product structure links
      await supabase.from('feedback_product_mapping').delete().eq('feedback_id', editingFeedback.id);
      if (editForm.product_item_ids.length > 0) {
        await supabase.from('feedback_product_mapping').insert(
          editForm.product_item_ids.map((productItemId) => ({
            feedback_id: editingFeedback.id,
            product_item_id: productItemId,
            product_id: null,
          }))
        );
      }

      // Update linked ideas
      await supabase.from('feedback_ideas').delete().eq('feedback_id', editingFeedback.id);
      if (editForm.linked_idea_ids.length > 0) {
        await supabase.from('feedback_ideas').insert(
          editForm.linked_idea_ids.map((ideaId) => ({
            feedback_id: editingFeedback.id,
            idea_id: ideaId,
          }))
        );
      }

      toast.success('Feedback updated successfully');
      setEditDialogOpen(false);
      setEditingFeedback(null);
      loadData();
    } catch (error: any) {
      console.error('Error updating feedback:', error);
      toast.error('Failed to update feedback', {
        description: error.message
      });
    }
  };

  const handleDeleteFeedback = async (id: string) => {
    try {
      const { error } = await supabase
        .from('feedback')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Feedback deleted successfully');
      loadData(); // Reload data
    } catch (error: any) {
      console.error('Error deleting feedback:', error);
      toast.error('Failed to delete feedback', {
        description: error.message
      });
    }
  };

  const handleBulkAction = async (action: string, selectedIds: (string | number)[]) => {
    try {
      if (action === 'delete') {
        const { error } = await supabase
          .from('feedback')
          .delete()
          .in('id', selectedIds);

        if (error) throw error;

        toast.success(`Deleted ${selectedIds.length} feedback items`);
        loadData();
      } else if (action === 'archive') {
        const { error } = await supabase
          .from('feedback')
          .update({ status: 'Archived' })
          .in('id', selectedIds);

        if (error) throw error;

        toast.success(`Archived ${selectedIds.length} feedback items`);
        loadData();
      } else {
        toast.success(`${action} applied to ${selectedIds.length} feedback items`);
      }
    } catch (error: any) {
      console.error(`Error performing bulk action:`, error);
      toast.error(`Failed to ${action} feedback items`, {
        description: error.message
      });
    }
  };

  const handleRefresh = () => {
    loadData();
    toast.success('Feedback data refreshed!');
  };

  const handleExport = () => {
    // Create CSV export
    const headers = ['ID', 'Source', 'Customer', 'Contributor', 'Content', 'Sentiment', 'Status', 'Created'];
    const csvContent = [
      headers.join(','),
      ...feedbackItems.map(item => [
        item.id,
        item.source,
        item.client?.name || 'Unknown',
        item.contributor,
        `"${item.content.replace(/"/g, '""')}"`,
        item.sentiment,
        item.status,
        new Date(item.createdAt).toISOString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feedback-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();

    toast.success('Feedback data exported successfully!');
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'bg-green-600';
      case 'negative': return 'bg-red-600';
      case 'neutral': return 'bg-blue-600';
      default: return 'bg-gray-600';
    }
  };

  const getSentimentBadge = (sentiment: string) => {
    const styles = {
      'positive': 'bg-green-50 text-green-600 border-green-200',
      'negative': 'bg-red-50 text-red-600 border-red-200',
      'neutral': 'bg-blue-50 text-blue-600 border-blue-200'
    };
    return styles[sentiment as keyof typeof styles] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const getPriorityDot = (priority?: number) => {
    if (!priority) return null;
    const colors = {
      1: 'bg-gray-400',
      2: 'bg-blue-500',
      3: 'bg-yellow-500',
      4: 'bg-orange-500',
      5: 'bg-red-500'
    };
    return (
      <div className="flex items-center space-x-1">
        <div className={`h-2 w-2 rounded-full ${colors[priority as keyof typeof colors]}`} />
        <span className="text-sm">{priority}</span>
      </div>
    );
  };

  const StarRating = ({ rating }: { rating?: number }) => {
    if (!rating) return <span className="text-sm text-muted-foreground">No rating</span>;
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star} 
            className={`h-4 w-4 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
          />
        ))}
      </div>
    );
  };

  // Table configuration
  const columns: ColumnDef<FeedbackItem>[] = [
    {
      key: 'content',
      label: 'Feedback',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'search',
          key: 'content',
          label: 'Search',
          placeholder: 'Search feedback content...'
        }
      ],
      width: 'min-w-[400px]',
      render: (item) => (
        <div className="space-y-2">
          <div className="font-medium">{item.content.length > 100 ? `${item.content.substring(0, 100)}...` : item.content}</div>
          <div className="flex flex-wrap gap-1">
            {item.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {item.tags.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{item.tags.length - 3}
              </Badge>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'customer',
      label: 'Customer',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'search',
          key: 'customer',
          label: 'Search',
          placeholder: 'Search customer name...'
        },
        {
          type: 'search',
          key: 'contributor',
          label: 'Contributor',
          placeholder: 'Search contributor name...'
        }
      ],
      render: (item) => (
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Building className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{item.client?.name || 'Unknown'}</span>
          </div>
          <div className="flex items-center space-x-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{item.contributor}</span>
          </div>
        </div>
      )
    },
    {
      key: 'clientArr',
      label: 'Client ARR',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      filterConfig: [
        {
          type: 'select',
          key: 'clientArr',
          label: 'Client ARR',
          options: [...new Set(feedbackItems.map(f => f.clientArr).filter((v): v is number => v != null && !isNaN(v)))].sort((a, b) => a - b).map(v => ({ value: String(v), label: formatCurrency(v, regionalSettings) }))
        }
      ],
      render: (item) => (item.clientArr != null ? formatCurrency(item.clientArr, regionalSettings) : '—')
    },
    {
      key: 'source',
      label: 'Source',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'select',
          key: 'source',
          label: 'Source',
          options: [...new Set(feedbackItems.map(f => f.source).filter(Boolean))].sort().map(v => ({ value: v, label: v }))
        }
      ],
      render: (item) => (
        <Badge variant="outline" className="text-xs">
          {item.source}
        </Badge>
      )
    },
    {
      key: 'sentiment',
      label: 'Sentiment',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'select',
          key: 'sentiment',
          label: 'Sentiment',
          options: [
            { value: 'positive', label: 'Positive' },
            { value: 'neutral', label: 'Neutral' },
            { value: 'negative', label: 'Negative' }
          ]
        }
      ],
      render: (item) => (
        <div className="flex items-center space-x-2">
          <div className={`h-2 w-2 rounded-full ${getSentimentColor(item.sentiment)}`} />
          <Badge className={getSentimentBadge(item.sentiment)}>
            {item.sentiment.charAt(0).toUpperCase() + item.sentiment.slice(1)}
          </Badge>
        </div>
      )
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      filterConfig: [
        {
          type: 'select',
          key: 'priority',
          label: 'Priority',
          options: [
            { value: '1', label: '1 - Lowest' },
            { value: '2', label: '2 - Low' },
            { value: '3', label: '3 - Medium' },
            { value: '4', label: '4 - High' },
            { value: '5', label: '5 - Critical' }
          ]
        }
      ],
      render: (item) => getPriorityDot(item.priority)
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'select',
          key: 'status',
          label: 'Status',
          options: [...new Set(feedbackItems.map(f => f.status).filter(Boolean))].sort().map(v => ({ value: v, label: v }))
        }
      ],
      render: (item) => (
        <Badge variant="outline">{item.status}</Badge>
      )
    },
    {
      key: 'rating',
      label: 'Rating',
      sortable: true,
      filterable: true,
      defaultVisible: false,
      filterConfig: [
        {
          type: 'select',
          key: 'rating',
          label: 'Rating',
          options: [
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
            { value: '5', label: '5' }
          ]
        }
      ],
      render: (item) => <StarRating rating={item.rating} />
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      filterable: true,
      filterConfig: [
        {
          type: 'dateRange',
          key: 'createdAt',
          label: 'Date Range'
        }
      ],
      render: (item) => (
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{formatDate(item.createdAt, regionalSettings)}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: '',
      width: 'w-[50px]',
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onViewDetails(item.id)}>
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
            {hasPermission(userRole, 'canUpdateFeedback') && (
              <>
                <DropdownMenuItem>
                  <Link className="h-4 w-4 mr-2" />
                  Link to Idea
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleEditFeedback(item)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              </>
            )}
            {hasPermission(userRole, 'canDeleteFeedback') && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="text-destructive"
                  onClick={() => handleDeleteFeedback(item.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  const bulkActions: BulkAction[] = [
    ...(hasPermission(userRole, 'canUpdateFeedback') ? [{
      key: 'archive',
      label: 'Archive',
      icon: <Archive className="h-4 w-4 mr-2" />,
      variant: 'outline' as const
    },
    {
      key: 'tag',
      label: 'Add Tags',
      icon: <TagIcon className="h-4 w-4 mr-2" />,
      variant: 'outline' as const
    }] : []),
    ...(hasPermission(userRole, 'canDeleteFeedback') ? [{
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4 mr-2" />,
      variant: 'destructive' as const,
      requireConfirmation: true,
      confirmTitle: 'Delete Selected Feedback',
      confirmDescription: 'Are you sure you want to delete the selected feedback items? This action cannot be undone.'
    }] : [])
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 min-w-0">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold">Feedback Management</h1>
          <p className="text-muted-foreground">Review and process customer feedback from all sources</p>
        </div>
        {hasPermission(userRole, 'canCreateFeedback') && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
              <SheetTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Feedback
                </Button>
              </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Add Manual Feedback</SheetTitle>
                <SheetDescription>
                  Manually add feedback from calls, meetings, or other sources
                </SheetDescription>
              </SheetHeader>
              
              <SheetBody>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="customer">Customer *</Label>
                      <Select value={newFeedback.client_id} onValueChange={(value) => setNewFeedback({ ...newFeedback, client_id: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contributor">Contributor *</Label>
                      <Input 
                        id="contributor" 
                        placeholder="Contributor name" 
                        value={newFeedback.contributor}
                        onChange={(e) => setNewFeedback({ ...newFeedback, contributor: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="content">Content *</Label>
                    <Textarea 
                      id="content" 
                      placeholder="Enter the feedback content..." 
                      rows={4}
                      value={newFeedback.content}
                      onChange={(e) => setNewFeedback({ ...newFeedback, content: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="source">Source</Label>
                      <Input 
                        id="source" 
                        placeholder="e.g., Support Ticket, Email, Survey" 
                        value={newFeedback.source}
                        onChange={(e) => setNewFeedback({ ...newFeedback, source: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sentiment">Sentiment</Label>
                      <Select value={newFeedback.sentiment} onValueChange={(value: any) => setNewFeedback({ ...newFeedback, sentiment: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select sentiment" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="positive">Positive</SelectItem>
                          <SelectItem value="neutral">Neutral</SelectItem>
                          <SelectItem value="negative">Negative</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority</Label>
                      <Select value={String(newFeedback.priority)} onValueChange={(value) => setNewFeedback({ ...newFeedback, priority: Number(value) as 1 | 2 | 3 | 4 | 5 })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 - Lowest</SelectItem>
                          <SelectItem value="2">2 - Low</SelectItem>
                          <SelectItem value="3">3 - Medium</SelectItem>
                          <SelectItem value="4">4 - High</SelectItem>
                          <SelectItem value="5">5 - Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rating">Rating</Label>
                      <Input 
                        id="rating" 
                        type="number" 
                        min="1" 
                        max="5" 
                        placeholder="Optional"
                        value={newFeedback.rating || ''}
                        onChange={(e) => setNewFeedback({ ...newFeedback, rating: e.target.value ? Number(e.target.value) : undefined })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select value={newFeedback.status} onValueChange={(value) => setNewFeedback({ ...newFeedback, status: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="New">New</SelectItem>
                          <SelectItem value="Reviewed">Reviewed</SelectItem>
                          <SelectItem value="Planned">Planned</SelectItem>
                          <SelectItem value="Archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <ProductNodeSelector
                      selectedNodeIds={newFeedback.product_item_ids}
                      onSelectionChange={(ids) => setNewFeedback({ ...newFeedback, product_item_ids: ids })}
                      label="Product area"
                      description="Select which parts of the product this feedback relates to"
                      allowProductSelection={false}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Link to Ideas</Label>
                    <IdeaSelectorForFeedback
                    ideas={ideas}
                    selectedIdeaIds={newFeedback.linked_idea_ids}
                    onSelectionChange={(ids) => setNewFeedback({ ...newFeedback, linked_idea_ids: ids })}
                    />
                  </div>
                </div>
              </SheetBody>
              
              <SheetFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateFeedback}>Save Feedback</Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
          <ImportFeedbackDialog
            open={importDialogOpen}
            onOpenChange={setImportDialogOpen}
            onSuccess={loadData}
          />
          </div>
        )}
      </div>

      {/* Advanced Data Table */}
      <AdvancedDataTableV2
        title="Customer Feedback"
        description="All customer feedback collected from various sources"
        data={feedbackItems}
        columns={columns}
        bulkActions={bulkActions}
        pageSize={50}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onBulkAction={handleBulkAction}
        onRowClick={(item) => onViewDetails(item.id)}
        emptyStateTitle="No feedback found"
        emptyStateDescription="Try adjusting your search or filters"
        emptyStateIcon={<MessageSquare className="h-8 w-8" />}
        defaultExclude={{ status: ['Archived'] }}
      />

      {/* Edit Dialog */}
      <Sheet open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit Feedback</SheetTitle>
            <SheetDescription>
              Update feedback details
            </SheetDescription>
          </SheetHeader>
          
          <SheetBody>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-customer">Customer</Label>
                  <Select value={editForm.client_id} onValueChange={(value) => setEditForm({ ...editForm, client_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-contributor">Contributor</Label>
                  <Input 
                    id="edit-contributor" 
                    placeholder="Contributor name" 
                    value={editForm.contributor}
                    onChange={(e) => setEditForm({ ...editForm, contributor: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-content">Content</Label>
                <Textarea 
                  id="edit-content" 
                  placeholder="Enter the feedback content..." 
                  rows={4}
                  value={editForm.content}
                  onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-source">Source</Label>
                  <Input 
                    id="edit-source" 
                    placeholder="e.g., Support Ticket, Email, Survey" 
                    value={editForm.source}
                    onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-sentiment">Sentiment</Label>
                  <Select value={editForm.sentiment} onValueChange={(value: any) => setEditForm({ ...editForm, sentiment: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sentiment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="positive">Positive</SelectItem>
                      <SelectItem value="neutral">Neutral</SelectItem>
                      <SelectItem value="negative">Negative</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-priority">Priority</Label>
                  <Select value={String(editForm.priority)} onValueChange={(value) => setEditForm({ ...editForm, priority: Number(value) as 1 | 2 | 3 | 4 | 5 })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Lowest</SelectItem>
                      <SelectItem value="2">2 - Low</SelectItem>
                      <SelectItem value="3">3 - Medium</SelectItem>
                      <SelectItem value="4">4 - High</SelectItem>
                      <SelectItem value="5">5 - Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-rating">Rating</Label>
                  <Input 
                    id="edit-rating" 
                    type="number" 
                    min="1" 
                    max="5" 
                    placeholder="Optional"
                    value={editForm.rating ?? ''}
                    onChange={(e) => setEditForm({ ...editForm, rating: e.target.value ? Number(e.target.value) : undefined })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-status">Status</Label>
                  <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Reviewed">Reviewed</SelectItem>
                      <SelectItem value="Planned">Planned</SelectItem>
                      <SelectItem value="Archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <ProductNodeSelector
                  selectedNodeIds={editForm.product_item_ids}
                  onSelectionChange={(ids) => setEditForm({ ...editForm, product_item_ids: ids })}
                  label="Product area"
                  description="Select which parts of the product this feedback relates to"
                  allowProductSelection={false}
                />
              </div>

              <div className="space-y-2">
                <Label>Link to Ideas</Label>
                <IdeaSelectorForFeedback
                  ideas={ideas}
                  selectedIdeaIds={editForm.linked_idea_ids}
                  onSelectionChange={(ids) => setEditForm({ ...editForm, linked_idea_ids: ids })}
                />
              </div>
            </div>
          </SheetBody>
          
          <SheetFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateFeedback}>
              Save Changes
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}